import {
  ButtonGroup,
  SelectOption,
  StyledText,
} from '@leasequery/lq.react-component-library';
import React, { ChangeEvent, useCallback } from 'react';
import { SWITCH_VALUES } from '../../../util/constants';
import * as Styled from './styled';
import { Icon } from '../../../components';
import { useAuth } from '../../../context';
import { getDateFormatByLocale, getDateMaskByLocale, getPlaceHolderFromDateFormat } from '../../../util/dateHelpers';

export interface ReportSettingPITState {
  pitSwitchValue?: SelectOption<boolean>;
  pitAsOfDate?: string;
  pitErrors?: string[];
  pitIsInvalid?: boolean;
}

interface ReportSettingPITInputProps {
  onChange?: (updatedValue: ReportSettingPITState) => void;
  pitState: ReportSettingPITState;
}

const BUTTON_GROUP_OPTIONS: SelectOption<boolean>[] = Object.values(
  SWITCH_VALUES,
);

function ReportSettingPITInput({
  onChange,
  pitState = {},
}: ReportSettingPITInputProps): JSX.Element {
  const { user } = useAuth();
  const localePreference = user.localePreference;
  const dateFormat = getDateFormatByLocale(localePreference);
  const placeholder = getPlaceHolderFromDateFormat(dateFormat);
  const inputMask = getDateMaskByLocale(localePreference);

  const {
    pitSwitchValue = BUTTON_GROUP_OPTIONS[0],
    pitAsOfDate = '',
    pitErrors = [] as string[],
  } = pitState;
  const pitSwitchChangeHandler = useCallback(
    (_, option: SelectOption<boolean>) => {
      onChange({ ...pitState, pitSwitchValue: option });
    },
    [onChange, pitState],
  );
  const asOfDateInputChangeHandler = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onChange({ ...pitState, pitAsOfDate: event.target.value });
    },
    [onChange, pitState],
  );

  const filteredPitErrors = pitErrors.filter((err) => !!err);

  return (
    <Styled.ReportSettingPITInput>
      <div>
        <Icon name="pit" />
      </div>
      <div className="pitSection">
        <StyledText as="div" fontWeight="medium">
          Point in Time
        </StyledText>
        <StyledText as="div" className="pitDescription">
          If enabled, Point in Time allows you to run the report with the
          historical lease information available as of the date selected.
          <br />
          If disabled, the report will run with current information
        </StyledText>
        {pitSwitchValue.value && (
          <div className="shortDate">
            <Styled.PITAsOfDateInput
              placeholder={placeholder}
              required={true}
              name="asOf"
              value={pitAsOfDate}
              onChange={asOfDateInputChangeHandler}
              mask={inputMask}
              label="As of"
              hasError={!!filteredPitErrors.length}
            />
            {!!filteredPitErrors.length && (
              <Styled.PITErrorsWrapper>
                {filteredPitErrors?.map((err) => (
                  <li key={err}>
                    <span>{err}</span>
                  </li>
                ))}
              </Styled.PITErrorsWrapper>
            )}
          </div>
        )}
      </div>
      <div className="pitButtonSwitch">
        <ButtonGroup
          options={BUTTON_GROUP_OPTIONS}
          selectedOption={pitSwitchValue}
          onClick={pitSwitchChangeHandler}
          name="pit-switch"
        />
      </div>
    </Styled.ReportSettingPITInput>
  );
}

export default ReportSettingPITInput;
